// Tebex Headless API client + Tebex.js loader.
//
// The storefront talks directly to Tebex from the browser — that's what the
// Headless API is built for. It's identified by the webstore's PUBLIC token
// (tenant.tebex.publicToken), which is safe to expose. The secret/Plugin API
// key is never used here.
//
// Docs: https://docs.tebex.io/developers/headless-api/endpoints
//       https://docs.tebex.io/developers/tebex.js

const BASE = 'https://headless.tebex.io/api'

// Currency formatting shared by the store page and cart drawer.
export function formatPrice(amount, currency) {
  if (amount == null) return ''
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(amount)
  } catch {
    return `${amount} ${currency || ''}`.trim()
  }
}

async function json(res, what) {
  const body = await res.json().catch(() => null)
  if (!res.ok) {
    // Surface Tebex's reason. field_details says WHICH option/field is invalid.
    let msg = body?.detail || body?.title || `Tebex ${what} failed (HTTP ${res.status})`
    if (Array.isArray(body?.field_details) && body.field_details.length) {
      msg += ` — ${body.field_details.map((f) => f.field ?? f.message ?? JSON.stringify(f)).join(', ')}`
    }
    if (typeof console !== 'undefined') console.error(`Tebex ${what} error:`, body)
    throw new Error(msg)
  }
  return body?.data ?? body
}

// ---- Catalogue -------------------------------------------------------------

// Categories with their packages nested, in store display order.
export async function getCategories(token) {
  const res = await fetch(`${BASE}/accounts/${token}/categories?includePackages=1`)
  return json(res, 'categories')
}

// ---- Basket ----------------------------------------------------------------

export async function createBasket(token, { completeUrl, cancelUrl }) {
  const res = await fetch(`${BASE}/accounts/${token}/baskets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      complete_url: completeUrl,
      cancel_url: cancelUrl,
      // Embedded checkout reports completion via a Tebex.js event, so we don't
      // want Tebex to hard-redirect the tab out from under us.
      complete_auto_redirect: false,
    }),
  })
  return json(res, 'basket create')
}

export async function getBasket(token, ident) {
  const res = await fetch(`${BASE}/accounts/${token}/baskets/${ident}`)
  return json(res, 'basket fetch')
}

// Basket package endpoints are token-less — the basket ident is the key.
export async function addPackage(ident, packageId, quantity = 1, variableData) {
  // package_id must be a STRING. FiveM (game-server-command) packages also
  // require the linked player's username_id in variable_data — without it Tebex
  // returns "One of the options provided is invalid".
  const body = { package_id: String(packageId), quantity }
  if (variableData && Object.keys(variableData).length) body.variable_data = variableData
  const res = await fetch(`${BASE}/baskets/${ident}/packages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  })
  return json(res, 'add package')
}

export async function removePackage(ident, packageId) {
  // package_id is a STRING here too, matching addPackage and the Headless spec.
  const res = await fetch(`${BASE}/baskets/${ident}/packages/remove`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ package_id: String(packageId) }),
  })
  return json(res, 'remove package')
}

// Auth providers (e.g. FiveM/Steam account linking) the buyer may need to
// complete before checkout, depending on the store's package config. Returns
// [{ name, url }]. Currently exposed for future use; embedded checkout handles
// most flows itself.
export async function getAuthLinks(token, ident, returnUrl) {
  const res = await fetch(
    `${BASE}/accounts/${token}/baskets/${ident}/auth?returnUrl=${encodeURIComponent(returnUrl)}`,
  )
  return json(res, 'auth links')
}

// ---- Tebex.js (embedded checkout) ------------------------------------------

let tebexJsPromise
// Inject the Tebex.js CDN script once and resolve the global `Tebex`.
export function loadTebexJs() {
  if (typeof window !== 'undefined' && window.Tebex) return Promise.resolve(window.Tebex)
  if (tebexJsPromise) return tebexJsPromise
  tebexJsPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = 'https://js.tebex.io/v/1.js'
    s.defer = true
    s.onload = () => resolve(window.Tebex)
    s.onerror = () => reject(new Error('Failed to load Tebex.js'))
    document.head.appendChild(s)
  })
  return tebexJsPromise
}
